import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api/axios';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  MagnifyingGlass, Package, Buildings, Receipt, ArrowsCounterClockwise,
  CheckCircle, XCircle, WarningCircle, Tag, Cube, Barcode,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const Q = (n) => `Q ${Number(n || 0).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const stockBadge = (qty) => {
  const n = Number(qty || 0);
  if (n <= 0) return { label: 'Agotado', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', Icon: XCircle };
  if (n < 5) return { label: 'Bajo', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', Icon: WarningCircle };
  return { label: 'OK', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', Icon: CheckCircle };
};

const StockBySucursal = ({ existencias }) => {
  if (!existencias || existencias.length === 0) {
    return <p className="text-xs text-[var(--text-muted)] italic">Sin información de existencias</p>;
  }
  // Group by sucursal
  const bySucursal = existencias.reduce((acc, e) => {
    const key = e.sucursal_nombre || e.sucursal || 'Sucursal';
    if (!acc[key]) acc[key] = { name: key, total: 0, bodegas: [] };
    const qty = Number(e.existencia || e.stock || 0);
    acc[key].total += qty;
    acc[key].bodegas.push({ name: e.bodega_nombre || e.bodega || '—', qty });
    return acc;
  }, {});
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
      {Object.values(bySucursal).map((s, i) => {
        const b = stockBadge(s.total);
        return (
          <div key={i} className="p-2 rounded border border-[var(--border)] bg-[var(--surface)]">
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="text-xs font-bold text-[var(--text)] truncate flex items-center gap-1">
                <Buildings size={11} /> {s.name}
              </div>
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded inline-flex items-center gap-0.5 ${b.cls}`}>
                <b.Icon size={10} weight="fill" /> {s.total}
              </span>
            </div>
            <div className="space-y-0.5">
              {s.bodegas.map((bd, j) => (
                <div key={j} className="flex justify-between text-[11px] text-[var(--text-muted)]">
                  <span className="truncate">{bd.name}</span>
                  <span className={`font-bold ${bd.qty <= 0 ? 'text-red-500' : bd.qty < 5 ? 'text-amber-600' : 'text-[var(--text)]'}`}>{bd.qty}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ProductCard = ({ product, onCotizar }) => {
  const totalStock = useMemo(() => {
    return (product.existencias || []).reduce((acc, e) => acc + Number(e.existencia || e.stock || 0), 0);
  }, [product]);
  const b = stockBadge(totalStock);
  return (
    <Card className="p-4 bg-[var(--surface)] border-[var(--border)]" data-testid={`product-${product.sku}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`text-[11px] font-bold px-2 py-0.5 rounded inline-flex items-center gap-1 ${b.cls}`}>
              <b.Icon size={12} weight="fill" /> Stock total: {totalStock}
            </span>
            <span className="text-[10px] text-[var(--text-muted)] inline-flex items-center gap-0.5">
              <Barcode size={10} /> {product.sku}
            </span>
            {product.categoria && (
              <span className="text-[10px] text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 px-1.5 py-0.5 rounded inline-flex items-center gap-0.5">
                <Tag size={9} /> {product.categoria}
              </span>
            )}
          </div>
          <h3 className="text-sm font-bold text-[var(--text)]">{product.descripcion || product.nombre}</h3>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <span className="text-lg font-black text-[#1D4ED8]">{Q(product.precio || product.precio_unitario || 0)}</span>
            {product.unidad && <span className="text-[10px] text-[var(--text-muted)]">/ {product.unidad}</span>}
          </div>
        </div>
        <Button size="sm" onClick={() => onCotizar(product)} className="bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0" data-testid={`cotizar-${product.sku}`}>
          <Receipt size={14} /> Cotizar
        </Button>
      </div>
      <StockBySucursal existencias={product.existencias} />
    </Card>
  );
};

export default function StockSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sucursales, setSucursales] = useState([]);
  const [sucursalId, setSucursalId] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const debounceRef = useRef(null);

  const loadSucursales = useCallback(async () => {
    try {
      const { data } = await api.get('/harperp/sucursales');
      setSucursales(data?.data || data || []);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { loadSucursales(); }, [loadSucursales]);

  const search = useCallback(async (q, p = 1, append = false) => {
    setLoading(true);
    try {
      const params = { q: q || undefined, page: p, per_page: 20 };
      if (sucursalId) params.sucursal_id = sucursalId;
      const { data } = await api.get('/harperp/productos', { params });
      const items = data?.data || [];
      setResults(append ? (prev) => [...prev, ...items] : items);
      setHasMore(items.length === 20);
      setTotal(data?.total || items.length);
      setPage(p);
    } catch (e) {
      toast.error('Error al buscar productos en HarpERP');
    } finally {
      setLoading(false);
    }
  }, [sucursalId]);

  // Debounced search on query change
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) { setResults([]); setTotal(0); return; }
    debounceRef.current = setTimeout(() => search(query.trim(), 1, false), 500);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, sucursalId, search]);

  const handleCotizar = (product) => {
    // Save the product in sessionStorage so Cotizador can pre-fill
    sessionStorage.setItem('cotizador_prefill_line', JSON.stringify({
      sku: product.sku,
      descripcion: product.descripcion || product.nombre,
      precio_unitario: Number(product.precio || product.precio_unitario || 0),
      cantidad: 1,
      unidad: product.unidad || 'UN',
      harperp_id: product.id || null,
    }));
    navigate('/cotizador?new=1');
  };

  return (
    <div className="p-4 max-w-6xl mx-auto space-y-4" data-testid="stock-search-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-[var(--text)] flex items-center gap-2">
            <Cube size={26} weight="duotone" className="text-blue-600" /> Stock — Búsqueda rápida
          </h1>
          <p className="text-xs text-[var(--text-muted)]">Consulta existencias y precios en vivo desde HarpERP</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => search(query, 1, false)} disabled={loading} data-testid="refresh-btn">
          <ArrowsCounterClockwise size={14} className={loading ? 'animate-spin' : ''} /> Refrescar
        </Button>
      </div>

      <Card className="p-3 bg-[var(--surface)] border-[var(--border)]">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por SKU o nombre del producto…"
              className="pl-9 h-10"
              autoFocus
              data-testid="search-input"
            />
          </div>
          <select
            value={sucursalId}
            onChange={(e) => setSucursalId(e.target.value)}
            className="h-10 px-3 rounded border border-[var(--border)] bg-[var(--surface)] text-[var(--text)] text-sm min-w-[180px]"
            data-testid="sucursal-select"
          >
            <option value="">Todas las sucursales</option>
            {sucursales.map((s) => (
              <option key={s.id} value={s.id}>{s.nombre || s.name}</option>
            ))}
          </select>
        </div>
        {total > 0 && (
          <p className="text-[11px] text-[var(--text-muted)] mt-2">
            <Package size={11} className="inline mr-1" />
            {total} producto{total !== 1 ? 's' : ''} {query && <>· "<b>{query}</b>"</>}
          </p>
        )}
      </Card>

      {loading && results.length === 0 ? (
        <Card className="p-8 text-center">
          <ArrowsCounterClockwise size={28} className="mx-auto animate-spin text-blue-600 mb-2" />
          <p className="text-sm text-[var(--text-muted)]">Buscando en HarpERP…</p>
        </Card>
      ) : results.length === 0 ? (
        <Card className="p-12 text-center">
          <Cube size={48} className="mx-auto text-[var(--text-muted)] opacity-40 mb-3" />
          <p className="text-sm text-[var(--text-muted)]">{query.trim() ? 'Sin resultados' : 'Escribe en la búsqueda para empezar'}</p>
          {!query.trim() && (
            <p className="text-[11px] text-[var(--text-faint)] mt-2">
              Ejemplos: "vidrio", "manija", "techo", o un código SKU directo
            </p>
          )}
        </Card>
      ) : (
        <div className="space-y-3">
          {results.map((p, i) => (
            <ProductCard key={`${p.sku}-${i}`} product={p} onCotizar={handleCotizar} />
          ))}
          {hasMore && (
            <div className="text-center">
              <Button onClick={() => search(query, page + 1, true)} variant="outline" disabled={loading} data-testid="load-more">
                {loading ? 'Cargando…' : 'Cargar más resultados'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
